import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV, COMPANY } from "@/data/content";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-[0_4px_30px_rgba(0,0,0,0.04)]"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 lg:h-24 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src="/brand/akyvia-logo.png"
              alt="Akyvia Sciences and Technologies logo"
              className="h-10 w-10 lg:h-11 lg:w-11 rounded-full object-contain ring-1 ring-slate-200 group-hover:ring-amber-400/60 group-hover:rotate-3 transition-all duration-300"
            />
            <div className="flex flex-col leading-[1.15]">
              <span className="font-display font-semibold text-[13px] sm:text-[15px] tracking-tight text-[#0B1B3D] whitespace-nowrap">
                {COMPANY.short.toUpperCase()}
              </span>
              <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-500 whitespace-nowrap">
                Sciences &amp;
              </span>
              <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-500 whitespace-nowrap">
                Technologies
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-underline text-sm font-medium tracking-tight transition-colors whitespace-nowrap ${
                    isActive
                      ? "text-[#0B1B3D] active"
                      : "text-slate-600 hover:text-[#0B1B3D]"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-contact"
              className="bg-[#0B1B3D] hover:bg-[#1E3A8A] text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors whitespace-nowrap"
            >
              Get in touch
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-nav-toggle"
            className="xl:hidden p-2 rounded-md text-[#0B1B3D] hover:bg-slate-100"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-nav-menu"
          className="xl:hidden bg-white border-t border-slate-200 shadow-lg"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 grid gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm font-medium ${
                    isActive
                      ? "bg-slate-100 text-[#0B1B3D]"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta-contact"
              className="mt-2 bg-[#0B1B3D] text-white text-sm font-medium px-4 py-3 rounded-md text-center"
            >
              Get in touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
